import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2, MapPin, Pencil, Trash2, X } from 'lucide-react';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

/*
 * Only name and city. Equipment — the one reader of service_locations — reads
 * nothing else. The endpoint also stores address, province, postal_code and
 * is_primary (with a set-primary route); they save and nothing reads them, so
 * this page neither shows nor sends them, and an edit leaves whatever a row
 * already holds untouched. Wire a reader up before bringing one back.
 *
 * The same rows are also written by /yards (ServiceLocationsScreen) through
 * /service-locations, by a different route with different rules.
 */
interface Location {
  id: string;
  name: string;
  city: string;
}

type LocationForm = Pick<Location, 'name' | 'city'>;

const EMPTY: LocationForm = { name: '', city: '' };

export default function LocationsPage() {
  const queryClient = useQueryClient();

  /*
   * Who may write, as opposed to who may read.
   *
   *   GET    /settings/locations       no role gate
   *   POST / PATCH / DELETE            requireRole('admin'), non-strict
   *
   * So admin, or a dispatcher on a tenant with dispatcher_admin_access — not
   * owner. The list itself is shown to everyone; only the controls hide.
   */
  const canWrite = useCanWriteCompanySettings();

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['settings', 'locations'],
    queryFn: () => apiClient.get('/settings/locations').then(r => r.data?.data ?? r.data ?? []),
  });

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<LocationForm>(EMPTY);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settings', 'locations'] });

  const createMutation = useMutation({
    mutationFn: (data: LocationForm) => apiClient.post('/settings/locations', data),
    onSuccess: () => { invalidate(); setShowForm(false); setForm(EMPTY); toast.success('Location added'); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to add location'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: LocationForm }) => apiClient.patch(`/settings/locations/${id}`, data),
    onSuccess: () => { invalidate(); setEditId(null); setForm(EMPTY); toast.success('Location updated'); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/settings/locations/${id}`),
    onSuccess: () => { invalidate(); toast.success('Location deleted'); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to delete'),
  });

  const startEdit = (loc: Location) => {
    setEditId(loc.id);
    setForm({ name: loc.name, city: loc.city });
    setShowForm(false);
  };

  const cancelEdit = () => { setEditId(null); setShowForm(false); setForm(EMPTY); };

  return (
    <div className="min-h-full">
      {/* The Organization workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4 max-w-2xl">
        {canWrite ? (
          <div className="flex justify-end">
            <button onClick={() => { setShowForm(true); setEditId(null); setForm(EMPTY); }}
              className="flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-lg">
              <Plus size={14} /> Add Location
            </button>
          </div>
        ) : (
          /* Say why there are no controls, rather than leaving a list that
             reads as one nobody is allowed to change. */
          <p className="text-xs text-fieldiq-muted">Only an admin can add, edit, or remove locations.</p>
        )}

        {isLoading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>}

        {/* Add / Edit Form */}
        {canWrite && (showForm || editId) && (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-white">{editId ? 'Edit Location' : 'New Location'}</span>
              <button onClick={cancelEdit} aria-label="Cancel" className="text-fieldiq-muted hover:text-white"><X size={16} /></button>
            </div>
            <input className={inputCls} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Location name (e.g. Main Yard)" aria-label="Location name" />
            <input className={inputCls} value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="City" aria-label="City" />
            <button
              onClick={() => editId ? updateMutation.mutate({ id: editId, data: form }) : createMutation.mutate(form)}
              disabled={!form.name.trim() || createMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold disabled:opacity-50">
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 size={14} className="animate-spin" />}
              {editId ? 'Update' : 'Add'}
            </button>
          </div>
        )}

        {/* List */}
        {!isLoading && (locations as Location[]).length === 0 && !showForm && (
          <div className="text-center py-12 text-fieldiq-muted">
            <MapPin size={36} className="mx-auto mb-3 opacity-30" />
            <p>No locations yet</p>
          </div>
        )}

        {(locations as Location[]).map((loc) => (
          <div key={loc.id} className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 flex items-start justify-between">
            <div>
              <span className="text-sm font-semibold text-white">{loc.name}</span>
              <p className="text-xs text-fieldiq-muted mt-0.5">{loc.city || '—'}</p>
            </div>
            {canWrite && (
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => startEdit(loc)} aria-label={`Edit ${loc.name}`} className="px-2 py-1 text-xs text-fieldiq-text hover:text-white"><Pencil size={14} /></button>
                <button onClick={() => { if (window.confirm('Delete this location?')) deleteMutation.mutate(loc.id); }}
                  aria-label={`Delete ${loc.name}`}
                  className="px-2 py-1 text-xs text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
